import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar, Box, Button, Chip, CircularProgress, Divider, Grid, IconButton, MenuItem, Select,
  Stack, Tab, Tabs, Tooltip, Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import AvTimerOutlinedIcon from "@mui/icons-material/AvTimerOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import FreeBreakfastOutlinedIcon from "@mui/icons-material/FreeBreakfastOutlined";
import api from "../../api/client";
import { endpoints } from "../../api/endpoints";
import { getBreakStatsForRecord, fmtDurFromMs, resolveAttendanceAsOf, resolveRecordWorkingHours } from "../../utils/attendanceBreakStats";
import { PageHeader, SectionCard, EmptyState, StatusChip } from "../ui";
import { AutoAbsentChip, isAutoAbsentLeave, leaveTypeLabel } from "../Leave/leaveAllocationUi";
import {
  DEFAULT_SATURDAY_POLICY,
  isSunday,
  isWeeklyOff,
  isCompanyOffDay,
  localDateKey,
} from "../../utils/weeklyOff";
import {
  SHIFT_TARGET_HOURS,
  FULL_DAY_MIN_HOURS,
  HALF_DAY_MIN_HOURS,
  FULL_DAY_MIN_LABEL,
  HALF_DAY_RANGE_LABEL,
  isFullDayByPolicy,
  isHalfDayByPolicy,
  attendanceStatusLabel,
} from "../../utils/attendanceDayPolicy";

/* ------- helpers ------- */
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const SHIFT_START_HOUR = 9.5;

function fmtTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}
function fmtDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
}
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function dateCellKey(date) {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}
function ymdToCellKey(ymd) {
  const [y, m, d] = String(ymd || "").split("-").map(Number);
  if (!y || !m || !d) return null;
  return `${y}-${m - 1}-${d}`;
}
function indexLeavesByDate(leaves) {
  const map = {};
  for (const lv of leaves || []) {
    const status = String(lv.status || "").toLowerCase();
    if (!["pending", "approved"].includes(status)) continue;
    if (lv.autoAbsentForDate) {
      const key = ymdToCellKey(lv.autoAbsentForDate);
      if (key) map[key] = lv;
      continue;
    }
    const start = new Date(lv.startDate);
    const end = new Date(lv.endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      map[dateCellKey(d)] = lv;
    }
  }
  return map;
}

function formatHoursDisplay(hours) {
  const totalMinutes = Math.round((hours || 0) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}h ${m}m`;
}

function AttendanceStatusChips({ hours }) {
  if (isFullDayByPolicy(hours)) {
    return <Chip label="Full day" size="small" sx={{ bgcolor: "#EDFAF2", color: "#065F46", fontWeight: 700 }} />;
  }
  if (isHalfDayByPolicy(hours)) {
    return <Chip label="Half day" size="small" sx={{ bgcolor: "#FEF3C7", color: "#92400E", fontWeight: 700 }} />;
  }
  return <Chip label="Short" size="small" sx={{ bgcolor: "#FEE2E2", color: "#991B1B", fontWeight: 700 }} />;
}

/* ------- main ------- */
export default function GetMyAttendanceNew() {
  const nav = useNavigate();
  const today = new Date();

  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(today);
  const [records, setRecords] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [satPolicy, setSatPolicy] = useState(DEFAULT_SATURDAY_POLICY);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const [rec, hol, shift, leaveRes] = await Promise.all([
        api.get("/api/attendance/attendancerecords").catch(() => ({ data: [] })),
        api.get("/api/holidays").catch(() => ({ data: [] })),
        api.get("/api/attendance-adv/my-shift").catch(() => ({ data: {} })),
        api.get(endpoints.leave.getLeaveRequest).catch(() => ({ data: [] })),
      ]);
      setRecords(Array.isArray(rec.data) ? rec.data : rec.data?.data || []);
      setHolidays(Array.isArray(hol.data) ? hol.data : hol.data?.data || []);
      setSatPolicy(shift.data?.saturdayPolicy || DEFAULT_SATURDAY_POLICY);
      setLeaves(Array.isArray(leaveRes.data) ? leaveRes.data : leaveRes.data?.data || []);
    } catch (err) {
      toast.error("Could not load attendance");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Records indexed by date key for fast lookup
  const byDate = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      const d = new Date(r.checkInTime || r.createdAt);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map[key]) map[key] = r;
    });
    return map;
  }, [records]);

  const holidayByDate = useMemo(() => {
    const map = {};
    holidays.forEach((h) => {
      const d = new Date(h.date);
      map[`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`] = h;
    });
    return map;
  }, [holidays]);

  const leaveByDate = useMemo(() => indexLeavesByDate(leaves), [leaves]);

  // Calendar grid for current view
  const calendarCells = useMemo(() => {
    const firstDay = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay();
    const daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), d);
      const key = dateCellKey(date);
      cells.push({
        date,
        record: byDate[key],
        holiday: holidayByDate[key],
        weeklyOff: isWeeklyOff(date, satPolicy),
        leave: leaveByDate[key],
      });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [viewDate, byDate, holidayByDate, satPolicy, leaveByDate]);

  // Month stats
  const monthStats = useMemo(() => {
    const mStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const mEnd = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
    const monthRecords = records.filter((r) => {
      const rd = new Date(r.checkInTime || r.createdAt);
      return rd >= mStart && rd < mEnd;
    });
    let present = 0, halfDays = 0, late = 0, overtime = 0, totalHours = 0, offsite = 0;
    let longestStreak = 0, currentStreak = 0;
    let punctualityEligible = 0;
    monthRecords.forEach((r) => {
      if (!r.checkInTime) return;
      const rd = new Date(r.checkInTime);
      const h = resolveRecordWorkingHours(r, resolveAttendanceAsOf(r, rd));
      totalHours += h;
      if (isFullDayByPolicy(h)) {
        present += 1;
        currentStreak += 1;
        longestStreak = Math.max(longestStreak, currentStreak);
      } else if (isHalfDayByPolicy(h)) {
        halfDays += 1;
        currentStreak = 0;
      } else {
        currentStreak = 0;
      }
      const ci = new Date(r.checkInTime);
      // Off days (Sunday / 1st–3rd Sat / holiday) do not count toward punctuality.
      if (!isCompanyOffDay(ci, { saturdayPolicy: satPolicy, holidayByKey: holidayByDate })) {
        punctualityEligible += 1;
        const ciMin = ci.getHours() * 60 + ci.getMinutes();
        if (ciMin > SHIFT_START_HOUR * 60 + 15) late += 1;
      }
      if (h > SHIFT_TARGET_HOURS) overtime += (h - SHIFT_TARGET_HOURS);
      if (r.checkInSource === "outside" || r.checkOutSource === "outside") offsite += 1;
    });

    // Working days in month (excluding Sundays + policy Saturdays + holidays), up to today if current month
    const isCurrent = viewDate.getMonth() === today.getMonth() && viewDate.getFullYear() === today.getFullYear();
    const endDate = isCurrent ? today : new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
    let workingDays = 0;
    let onLeave = 0;
    let autoAbsent = 0;
    for (let d = new Date(mStart); d <= endDate; d.setDate(d.getDate() + 1)) {
      if (isWeeklyOff(d, satPolicy)) continue;
      const holKey = localDateKey(d);
      const cellKey = dateCellKey(d);
      if (holidayByDate[holKey] || holidayByDate[cellKey]) continue;
      workingDays += 1;
      const leave = leaveByDate[cellKey];
      if (leave && !byDate[cellKey]) {
        if (isAutoAbsentLeave(leave)) autoAbsent += 1;
        else onLeave += 1;
      }
    }

    const absent = Math.max(0, workingDays - present - halfDays - onLeave - autoAbsent);
    const punctuality = punctualityEligible > 0
      ? Math.round(((punctualityEligible - late) / punctualityEligible) * 100)
      : 100;
    const attendanceRate = workingDays > 0 ? Math.round((present / workingDays) * 100) : 0;
    const avgHours = monthRecords.length ? totalHours / monthRecords.length : 0;

    return {
      present, halfDays, late, overtime: overtime.toFixed(1), absent, offsite,
      onLeave, autoAbsent,
      total: monthRecords.length, workingDays, totalHours,
      avgHours, punctuality, attendanceRate, longestStreak,
    };
  }, [records, viewDate, today, holidayByDate, satPolicy, leaveByDate, byDate]);

  // Daily hours series for the current month (for bar chart)
  const daysSeries = useMemo(() => {
    const mStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
    const out = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), d);
      const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      const rec = byDate[key];
      const hol = holidayByDate[key];
      const asOf = rec ? resolveAttendanceAsOf(rec, date) : null;
      out.push({
        date, hours: rec && asOf ? resolveRecordWorkingHours(rec, asOf) : 0,
        record: rec, holiday: hol, leave: leaveByDate[key],
      });
    }
    return out;
  }, [viewDate, byDate, holidayByDate, leaveByDate]);

  const selectedRecord = useMemo(() => {
    const key = dateCellKey(selectedDate);
    return byDate[key];
  }, [selectedDate, byDate]);
  const selectedHoliday = useMemo(() => {
    const key = dateCellKey(selectedDate);
    return holidayByDate[key];
  }, [selectedDate, holidayByDate]);
  const selectedLeave = useMemo(() => {
    const key = dateCellKey(selectedDate);
    return leaveByDate[key];
  }, [selectedDate, leaveByDate]);

  const exportCsv = () => {
    const headers = ["Date", "Day", "Check In", "Check Out", "Hours", "Status", "Source", "Location", "Reason"];
    const rows = records
      .sort((a, b) => new Date(b.checkInTime || b.createdAt) - new Date(a.checkInTime || a.createdAt))
      .map((r) => {
        const d = new Date(r.checkInTime || r.createdAt);
        const h = resolveRecordWorkingHours(r, resolveAttendanceAsOf(r, d));
        return [
          d.toLocaleDateString("en-IN"),
          d.toLocaleDateString("en-IN", { weekday: "short" }),
          r.checkInTime ? fmtTime(r.checkInTime) : "",
          r.checkOutTime ? fmtTime(r.checkOutTime) : "",
          h.toFixed(2),
          attendanceStatusLabel(h),
          r.checkInSource === "outside" || r.checkOutSource === "outside" ? "Off-site" : "Office",
          r.checkInLocation || r.checkOutLocation || "",
          r.checkInReason || r.checkOutReason || "",
        ];
      });
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `attendance-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("CSV downloaded");
  };

  const goPrev = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  const goNext = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  const goToday = () => { setViewDate(new Date(today.getFullYear(), today.getMonth(), 1)); setSelectedDate(today); };

  const isCurrentMonth = viewDate.getMonth() === today.getMonth() && viewDate.getFullYear() === today.getFullYear();

  return (
    <Box p={3}>
      <PageHeader
        title="My Attendance"
        subtitle="Calendar view, trends, and history"
        icon={<CalendarMonthOutlinedIcon />}
        actions={[
          <Button key="fix" variant="outlined" startIcon={<EditCalendarOutlinedIcon />} onClick={() => nav("/AttendanceRegularization")}>
            Request correction
          </Button>,
          <Button key="csv" variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={exportCsv}>
            Export CSV
          </Button>,
        ]}
      />

      {/* Month navigator + stats */}
      <Box
        sx={{
          p: 2.5, mb: 3, borderRadius: 4,
          background: "linear-gradient(135deg, #6C50BF 0%, #492ba3 100%)",
          color: "#fff", position: "relative", overflow: "hidden",
        }}
      >
        <Box sx={{ position: "absolute", right: -60, top: -60, width: 220, height: 220, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.08)" }} />
        <Grid container spacing={2} alignItems="center" sx={{ position: "relative" }}>
          <Grid item xs={12} md={4}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton onClick={goPrev} size="small" sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>
                <ChevronLeftIcon />
              </IconButton>
              <Box sx={{ flex: 1, textAlign: "center" }}>
                <Typography sx={{ fontSize: 11, opacity: 0.85, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>
                  Period
                </Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 800 }}>
                  {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
                </Typography>
              </Box>
              <IconButton onClick={goNext} size="small" sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>
                <ChevronRightIcon />
              </IconButton>
              {!isCurrentMonth && (
                <Button size="small" onClick={goToday} sx={{ ml: 1, color: "#fff", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>
                  Today
                </Button>
              )}
            </Stack>
          </Grid>
          <Grid item xs={12} md={8}>
            <Grid container spacing={1.5}>
              <HeroStat label="Attendance rate" value={`${monthStats.attendanceRate}%`} hint={`${monthStats.present} of ${monthStats.workingDays} days`} />
              <HeroStat label="Total hours" value={`${monthStats.totalHours.toFixed(1)}h`} hint={`Avg ${monthStats.avgHours.toFixed(1)}h/day`} />
              <HeroStat label="Punctuality" value={`${monthStats.punctuality}%`} hint={`${monthStats.late} late`} />
              <HeroStat label="Overtime" value={`${monthStats.overtime}h`} hint={`Beyond ${SHIFT_TARGET_HOURS}h/day`} />
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Tabs: Calendar / Trends */}
      <Box sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab icon={<CalendarMonthOutlinedIcon fontSize="small" />} iconPosition="start" label="Calendar" sx={{ minHeight: 48 }} />
          <Tab icon={<TrendingUpIcon fontSize="small" />} iconPosition="start" label="Trends" sx={{ minHeight: 48 }} />
          <Tab icon={<AvTimerOutlinedIcon fontSize="small" />} iconPosition="start" label={`Records (${records.length})`} sx={{ minHeight: 48 }} />
        </Tabs>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : tab === 0 ? (
        // ========== CALENDAR TAB ==========
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <SectionCard accent="#6C50BF">
              <Grid container spacing={0.5} mb={1}>
                {DAY_NAMES.map((d) => (
                  <Grid item xs={12 / 7} key={d} sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 0.5, py: 1 }}>
                      {d}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
              <Grid container spacing={0.5}>
                {calendarCells.map((cell, i) => (
                  <Grid item xs={12 / 7} key={i}>
                    {cell ? (
                      <CalendarCell
                        cell={cell}
                        isSelected={sameDay(cell.date, selectedDate)}
                        isToday={sameDay(cell.date, today)}
                        isFuture={cell.date > today}
                        onClick={() => setSelectedDate(cell.date)}
                      />
                    ) : (
                      <Box sx={{ aspectRatio: "1/1", bgcolor: "transparent" }} />
                    )}
                  </Grid>
                ))}
              </Grid>
              <Legend />
            </SectionCard>
          </Grid>

          {/* Selected day details */}
          <Grid item xs={12} md={4}>
            <SectionCard
              title={selectedDate.toLocaleDateString("en-IN", { weekday: "long" })}
              subtitle={selectedDate.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
              accent={sameDay(selectedDate, today) ? "#6C50BF" : "#5C85FF"}
            >
              <DayDetails
                record={selectedRecord}
                holiday={selectedHoliday}
                leave={selectedLeave}
                date={selectedDate}
                isFuture={selectedDate > today}
                satPolicy={satPolicy}
                onViewLeave={() => nav("/leave/my")}
              />
            </SectionCard>
          </Grid>
        </Grid>
      ) : tab === 1 ? (
        // ========== TRENDS TAB ==========
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <SectionCard title="Daily hours" subtitle={`${MONTHS[viewDate.getMonth()]} ${viewDate.getFullYear()}`} accent="#6C50BF">
              <MonthBarChart data={daysSeries} satPolicy={satPolicy} onSelect={(d) => { setSelectedDate(d); setTab(0); }} />
            </SectionCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <SectionCard title="Insights" accent="#F9BC06">
              <Stack spacing={1.5}>
                <InsightRow icon={<CheckCircleOutlineIcon />} color="#39C645" label="Present" value={monthStats.present} hint={`${monthStats.workingDays} working days`} />
                <InsightRow icon={<EventBusyOutlinedIcon />} color="#F97316" label="Auto-absent" value={monthStats.autoAbsent} hint="Marked after 3 working days" />
                <InsightRow icon={<BeachAccessOutlinedIcon />} color="#7C3AED" label="On leave" value={monthStats.onLeave} hint="Approved or pending leave" />
                <InsightRow icon={<CancelOutlinedIcon />} color="#EF4444" label="Absent" value={monthStats.absent} hint="Working days missed" />
                <InsightRow icon={<ScheduleOutlinedIcon />} color="#F9BC06" label="Half days" value={monthStats.halfDays} hint={HALF_DAY_RANGE_LABEL} />
                <InsightRow icon={<AvTimerOutlinedIcon />} color="#EF4444" label="Late arrivals" value={monthStats.late} hint={`After ${Math.floor(SHIFT_START_HOUR)}:${String(Math.round((SHIFT_START_HOUR % 1) * 60)).padStart(2, "0")} + 15m`} />
                <InsightRow icon={<BoltOutlinedIcon />} color="#6C50BF" label="Overtime" value={`${monthStats.overtime}h`} hint={`Beyond ${SHIFT_TARGET_HOURS}h/day`} />
                <InsightRow icon={<LocationOnOutlinedIcon />} color="#5C85FF" label="Off-site days" value={monthStats.offsite} hint="WFH or external" />
                <InsightRow icon={<TrendingUpIcon />} color="#39C645" label="Longest streak" value={`${monthStats.longestStreak}d`} hint="Consecutive full days" />
              </Stack>
            </SectionCard>
          </Grid>
        </Grid>
      ) : (
        // ========== RECORDS TAB ==========
        <SectionCard title="All records" subtitle={`${records.length} entries`}>
          {records.length === 0 ? (
            <EmptyState title="No attendance records yet" subtitle="Your check-ins will appear here." />
          ) : (
            <RecordsTable records={records} />
          )}
        </SectionCard>
      )}
    </Box>
  );
}

/* ------- sub-components ------- */

function HeroStat({ label, value, hint }) {
  return (
    <Grid item xs={6} sm={3}>
      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)" }}>
        <Typography sx={{ fontSize: 10, opacity: 0.85, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1, mt: 0.25 }}>{value}</Typography>
        <Typography sx={{ fontSize: 10, opacity: 0.8 }}>{hint}</Typography>
      </Box>
    </Grid>
  );
}

function CalendarCell({ cell, isSelected, isToday, isFuture, onClick }) {
  const { date, record, holiday, weeklyOff, leave } = cell;
  const weekend = weeklyOff != null ? weeklyOff : isSunday(date);
  const asOf = record ? resolveAttendanceAsOf(record, date) : null;
  const hours = record && asOf ? resolveRecordWorkingHours(record, asOf) : 0;
  const autoAbsent = !record && isAutoAbsentLeave(leave);
  const onLeave = !record && leave && !autoAbsent;

  let bg, border, fg;
  if (isFuture) { bg = "transparent"; border = "1px solid #E5E7EB"; fg = "#9CA3AF"; }
  else if (holiday && !record) { bg = "#E8F0FE"; border = "1px solid #8AB4F8"; fg = "#1A56DB"; }
  else if (weekend && !record) { bg = "#E8F0FE"; border = "1px solid #8AB4F8"; fg = "#1A56DB"; }
  else if (autoAbsent) { bg = "#FFEDD5"; border = "1px solid #FDBA74"; fg = "#9A3412"; }
  else if (onLeave) { bg = "#F3E8FF"; border = "1px solid #D8B4FE"; fg = "#6B21A8"; }
  else if (!record) { bg = "#FEE2E2"; border = "1px solid #FECACA"; fg = "#991B1B"; }
  else if (isFullDayByPolicy(hours)) { bg = "#EDFAF2"; border = "1px solid #A7F3D0"; fg = "#065F46"; }
  else if (isHalfDayByPolicy(hours)) { bg = "#FEF3C7"; border = "1px solid #FCD34D"; fg = "#92400E"; }
  else { bg = "#FEE2E2"; border = "1px solid #FECACA"; fg = "#991B1B"; }

  if (isSelected) { border = "2px solid #6C50BF"; }
  if (isToday) { border = "2px solid #6C50BF"; }

  const ci = record?.checkInTime ? new Date(record.checkInTime) : null;
  const ciMin = ci ? ci.getHours() * 60 + ci.getMinutes() : 0;
  const late = ci && ciMin > SHIFT_START_HOUR * 60 + 15;
  const offsite = record?.checkInSource === "outside" || record?.checkOutSource === "outside";

  return (
    <Tooltip
      title={
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
            {date.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short" })}
          </Typography>
          {holiday && <Typography sx={{ fontSize: 11 }}>🎉 {holiday.name}</Typography>}
          {weekend && !record && <Typography sx={{ fontSize: 11 }}>{isSunday(date) ? "Sunday" : "Weekly Off"}</Typography>}
          {autoAbsent && <Typography sx={{ fontSize: 11 }}>Auto-absent marked</Typography>}
          {onLeave && <Typography sx={{ fontSize: 11 }}>On leave · {leaveTypeLabel(leave.leaveType)}</Typography>}
          {record && (
            <>
              <Typography sx={{ fontSize: 11 }}>In: {fmtTime(record.checkInTime)}</Typography>
              <Typography sx={{ fontSize: 11 }}>Out: {record.checkOutTime ? fmtTime(record.checkOutTime) : "—"}</Typography>
              <Typography sx={{ fontSize: 11 }}>Net: {formatHoursDisplay(hours)}</Typography>
              <Typography sx={{ fontSize: 11 }}>
                {isFullDayByPolicy(hours) ? "Full day" : isHalfDayByPolicy(hours) ? "Half day" : hours > 0 ? "Short" : "Absent"}
              </Typography>
            </>
          )}
          {!record && !weekend && !holiday && !leave && !isFuture && (
            <Typography sx={{ fontSize: 11 }}>No record</Typography>
          )}
        </Box>
      }
    >
      <Box
        onClick={onClick}
        sx={{
          aspectRatio: "1/1", borderRadius: 1.5, bgcolor: bg, border,
          cursor: "pointer", position: "relative",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          transition: "all 0.15s",
          "&:hover": { transform: "translateY(-2px)", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: fg }}>
          {date.getDate()}
        </Typography>
        {record && hours > 0 && (
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: fg, lineHeight: 1 }}>
            {hours.toFixed(1)}h
          </Typography>
        )}
        {autoAbsent && (
          <Typography sx={{ fontSize: 8, fontWeight: 800, color: fg, lineHeight: 1, mt: 0.25 }}>AA</Typography>
        )}
        {onLeave && (
          <Typography sx={{ fontSize: 8, fontWeight: 800, color: fg, lineHeight: 1, mt: 0.25 }}>LV</Typography>
        )}
        {holiday && !record && (
          <Typography sx={{ fontSize: 9, color: fg, lineHeight: 1, mt: 0.25, fontWeight: 600 }}>🎉</Typography>
        )}
        {/* Flags */}
        <Stack direction="row" spacing={0.25} sx={{ position: "absolute", bottom: 3, right: 3 }}>
          {late && <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "#EF4444" }} />}
          {offsite && <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "#F59E0B" }} />}
        </Stack>
      </Box>
    </Tooltip>
  );
}

function Legend() {
  const items = [
    { color: "#86EFAC", label: `Full day (≥ ${FULL_DAY_MIN_LABEL})` },
    { color: "#FCD34D", label: `Half day (${HALF_DAY_RANGE_LABEL})` },
    { color: "#FCA5A5", label: "Absent / short" },
    { color: "#FDBA74", label: "Auto-absent" },
    { color: "#D8B4FE", label: "On leave" },
    { color: "#AECBFA", label: "Sunday" },
    { color: "#AECBFA", label: "Holiday", icon: "🎉" },
  ];
  return (
    <Stack direction="row" spacing={2} mt={2} pt={2} sx={{ borderTop: "1px solid", borderColor: "divider" }} flexWrap="wrap" useFlexGap>
      {items.map((it) => (
        <Stack key={it.label} direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 14, height: 14, borderRadius: 0.75, bgcolor: it.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8 }}>
            {it.icon}
          </Box>
          <Typography sx={{ fontSize: 10, color: "text.secondary" }}>{it.label}</Typography>
        </Stack>
      ))}
      <Box sx={{ flex: 1 }} />
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#EF4444" }} />
        <Typography sx={{ fontSize: 10, color: "text.secondary" }}>Late</Typography>
      </Stack>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#F59E0B" }} />
        <Typography sx={{ fontSize: 10, color: "text.secondary" }}>Off-site</Typography>
      </Stack>
    </Stack>
  );
}

function DayDetails({ record, holiday, leave, date, isFuture, satPolicy = DEFAULT_SATURDAY_POLICY, onViewLeave }) {
  const weekend = isWeeklyOff(date, satPolicy) || !!holiday;
  const asOf = record ? resolveAttendanceAsOf(record, date) : null;
  const hours = record && asOf ? resolveRecordWorkingHours(record, asOf) : 0;
  const autoAbsent = isAutoAbsentLeave(leave);

  if (isFuture) {
    return (
      <EmptyState
        icon={<ScheduleOutlinedIcon />}
        title="Upcoming day"
        subtitle="This day hasn't arrived yet."
      />
    );
  }
  if (holiday) {
    return (
      <Stack spacing={2} alignItems="center" textAlign="center" py={2}>
        <Box sx={{ width: 60, height: 60, borderRadius: "50%", bgcolor: "#E8F0FE", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontSize: 28 }}>🎉</Typography>
        </Box>
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{holiday.name}</Typography>
        <Chip label="Company Holiday" sx={{ bgcolor: "#AECBFA", color: "#1A56DB", fontWeight: 700 }} />
        {record && (
          <>
            <Divider flexItem />
            <AttendanceStatusChips hours={hours} />
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              Net working time: <b>{formatHoursDisplay(hours)}</b>
            </Typography>
          </>
        )}
      </Stack>
    );
  }
  if (!record && leave) {
    return (
      <Stack spacing={1.75} alignItems="center" textAlign="center" py={1.5}>
        <Box sx={{
          width: 60, height: 60, borderRadius: "50%",
          bgcolor: autoAbsent ? "#FFEDD5" : "#F3E8FF",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {autoAbsent
            ? <EventBusyOutlinedIcon sx={{ color: "#EA580C", fontSize: 32 }} />
            : <BeachAccessOutlinedIcon sx={{ color: "#7C3AED", fontSize: 32 }} />}
        </Box>
        {autoAbsent ? <AutoAbsentChip /> : <Chip label="On leave" sx={{ bgcolor: "#F3E8FF", color: "#6B21A8", fontWeight: 700 }} />}
        <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
          {autoAbsent ? "Auto-absent marked" : leaveTypeLabel(leave.leaveType)}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary", px: 1 }}>
          {autoAbsent
            ? `No check-in, leave, or regularization after 3 working days. ${leaveTypeLabel(leave.leaveType)} was applied automatically.`
            : `${leaveTypeLabel(leave.leaveType)} covers this date.`}
        </Typography>
        <StatusChip status={leave.status} />
        {leave.reason ? (
          <Typography sx={{ fontSize: 12, color: "text.secondary", px: 1 }}>
            {leave.reason}
          </Typography>
        ) : null}
        {onViewLeave ? (
          <Button size="small" variant="outlined" onClick={onViewLeave} sx={{ textTransform: "none", fontWeight: 700 }}>
            View leave request
          </Button>
        ) : null}
      </Stack>
    );
  }
  if (!record && weekend) {
    return (
      <Stack spacing={2} alignItems="center" textAlign="center" py={2}>
        <Box sx={{ width: 60, height: 60, borderRadius: "50%", bgcolor: "#E8F0FE", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <BeachAccessOutlinedIcon sx={{ color: "#1A56DB", fontSize: 32 }} />
        </Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Sunday</Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>No work expected today.</Typography>
      </Stack>
    );
  }
  if (!record) {
    return (
      <EmptyState
        icon={<CancelOutlinedIcon />}
        title="No record"
        subtitle="No check-in captured for this day."
      />
    );
  }

  const ci = new Date(record.checkInTime);
  const ciMin = ci.getHours() * 60 + ci.getMinutes();
  const late = ciMin > SHIFT_START_HOUR * 60 + 15;
  const offsite = record.checkInSource === "outside" || record.checkOutSource === "outside";
  const breakStats = getBreakStatsForRecord(record, asOf);
  const hasBreaks = breakStats.count > 0 || breakStats.totalBreakMs > 0;

  const workingExtra = [
    hasBreaks ? `Excludes ${fmtDurFromMs(breakStats.totalBreakMs)} break` : null,
    hours > SHIFT_TARGET_HOURS ? `${(hours - SHIFT_TARGET_HOURS).toFixed(1)}h overtime` : null,
  ].filter(Boolean).join(" · ") || null;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <AttendanceStatusChips hours={hours} />
        <Chip
          label={`Full ≥ ${FULL_DAY_MIN_LABEL} · Half ${HALF_DAY_RANGE_LABEL}`}
          size="small"
          variant="outlined"
          sx={{ fontSize: 10, fontWeight: 600, color: "text.secondary", borderColor: "divider" }}
        />
        {late && <Chip label="Late arrival" size="small" icon={<AvTimerOutlinedIcon />} sx={{ bgcolor: "#FEE2E2", color: "#991B1B", fontWeight: 700 }} />}
        {offsite && <Chip label="Off-site" size="small" icon={<LocationOnOutlinedIcon />} sx={{ bgcolor: "#FEF3C7", color: "#92400E", fontWeight: 700 }} />}
        {breakStats.onBreak && (
          <Chip label="On break" size="small" icon={<FreeBreakfastOutlinedIcon />} sx={{ bgcolor: "#FEF3C7", color: "#92400E", fontWeight: 700 }} />
        )}
      </Stack>

      <TimeBox icon={<LoginOutlinedIcon />} color="#39C645" bg="#EDFAF2" label="Check-in"
        time={fmtTime(record.checkInTime)}
        extra={record.checkInSource === "outside" ? [record.checkInLocation, record.checkInReason].filter(Boolean).join(" • ") : null}
      />
      <TimeBox icon={<LogoutOutlinedIcon />} color="#EF4444" bg="#FEE2E2" label="Check-out"
        time={record.checkOutTime ? fmtTime(record.checkOutTime) : "Not checked out"}
        extra={record.checkOutSource === "outside" ? [record.checkOutLocation, record.checkOutReason].filter(Boolean).join(" • ") : null}
      />

      {hasBreaks && (
        <Box sx={{ p: 1.75, borderRadius: 2, bgcolor: "#FFFBEB", border: "1px solid rgba(249,188,6,0.35)" }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <FreeBreakfastOutlinedIcon sx={{ color: "#D97706", fontSize: 20 }} />
            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#D97706", letterSpacing: 0.5 }}>
              Breaks ({breakStats.count})
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#92400E", ml: "auto" }}>
              {fmtDurFromMs(breakStats.totalBreakMs)} total
            </Typography>
          </Stack>
          <Stack spacing={0.75}>
            {breakStats.breaks.map((b, idx) => (
              <Box
                key={b.id}
                sx={{
                  py: 0.75, px: 1, borderRadius: 1.5,
                  bgcolor: "rgba(255,255,255,0.7)",
                  border: "1px solid rgba(249,188,6,0.2)",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={0.5}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary" }}>
                    {b.label || `Break ${idx + 1}`}
                    {b.isOpen ? " (open)" : ""}
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#92400E" }}>
                    {fmtDurFromMs(b.durationMs)}
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.25 }}>
                  {fmtTime(b.start)}
                  {b.end ? ` – ${fmtTime(b.end)}` : " – in progress"}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      <TimeBox icon={<AvTimerOutlinedIcon />} color="#6C50BF" bg="#F4EDFA" label="Working hours (net)"
        time={formatHoursDisplay(hours)}
        extra={workingExtra}
      />
      {hasBreaks && record.checkOutTime && (
        <Typography sx={{ fontSize: 11, color: "text.secondary", px: 0.5 }}>
          Time at desk: {fmtDurFromMs(breakStats.grossMs)} · Net work: {fmtDurFromMs(breakStats.netWorkingMs)}
        </Typography>
      )}
    </Stack>
  );
}

function TimeBox({ icon, color, bg, label, time, extra }) {
  return (
    <Box sx={{ p: 1.75, borderRadius: 2, bgcolor: bg, border: "1px solid", borderColor: `${color}40` }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
        <Box sx={{ color }}>{icon}</Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color, letterSpacing: 0.5 }}>
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 18, fontWeight: 700, color: "text.primary", ml: 3.5 }}>{time}</Typography>
      {extra && (
        <Typography sx={{ fontSize: 11, color: "text.secondary", ml: 3.5, mt: 0.25 }}>
          {extra}
        </Typography>
      )}
    </Box>
  );
}

function MonthBarChart({ data, onSelect, satPolicy = DEFAULT_SATURDAY_POLICY }) {
  const max = Math.max(10, ...data.map((d) => d.hours));
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5, height: 200, position: "relative" }}>
        {/* Full-day target line */}
        <Box sx={{ position: "absolute", left: 0, right: 0, bottom: `${(FULL_DAY_MIN_HOURS / max) * 100}%`, height: 1, bgcolor: "#6C50BF", opacity: 0.4, borderStyle: "dashed", borderWidth: "0 0 1px 0", borderColor: "#6C50BF" }}>
          <Typography sx={{ fontSize: 9, color: "#6C50BF", fontWeight: 700, position: "absolute", right: 0, top: -14 }}>{FULL_DAY_MIN_LABEL} full day</Typography>
        </Box>
        {data.map((d, i) => {
          const h = d.hours;
          const pct = (h / max) * 100;
          const weekend = isWeeklyOff(d.date, satPolicy);
          const isHoliday = !!d.holiday;
          const color = h === 0 ? "#E5E7EB" : isFullDayByPolicy(h) ? "#39C645" : isHalfDayByPolicy(h) ? "#6C50BF" : "#F9BC06";
          const today = new Date();
          const isToday = sameDay(d.date, today);
          return (
            <Tooltip
              key={i}
              title={
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                    {d.date.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
                  </Typography>
                  {isHoliday && <Typography sx={{ fontSize: 11 }}>🎉 {d.holiday.name}</Typography>}
                  {weekend && !d.record && <Typography sx={{ fontSize: 11 }}>{isSunday(d.date) ? "Sunday" : "Weekly Off"}</Typography>}
                  {d.record ? <Typography sx={{ fontSize: 11 }}>{h.toFixed(1)}h worked</Typography> : null}
                </Box>
              }
            >
              <Box
                onClick={() => onSelect?.(d.date)}
                sx={{
                  flex: 1, minWidth: 6, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end",
                  cursor: "pointer", height: "100%", position: "relative",
                  "&:hover > div:first-of-type": { filter: "brightness(1.1)" },
                }}
              >
                <Box sx={{
                  width: "100%", height: `${pct}%`, minHeight: 2,
                  bgcolor: isHoliday ? "#8AB4F8" : weekend && h === 0 ? "#8AB4F8" : color,
                  opacity: isHoliday ? 0.7 : 1,
                  borderRadius: "3px 3px 0 0",
                  transition: "all 0.2s",
                  border: isToday ? "2px solid #6C50BF" : "none",
                  borderBottom: "none",
                }} />
                <Typography sx={{ fontSize: 9, color: "text.secondary", mt: 0.25, fontWeight: isToday ? 700 : 400 }}>
                  {d.date.getDate()}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Stack direction="row" spacing={2} mt={2} pt={1.5} sx={{ borderTop: "1px solid", borderColor: "divider" }} flexWrap="wrap" useFlexGap>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: "#39C645" }} />
          <Typography sx={{ fontSize: 10, color: "text.secondary" }}>{`Full (≥ ${FULL_DAY_MIN_LABEL})`}</Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: "#6C50BF" }} />
          <Typography sx={{ fontSize: 10, color: "text.secondary" }}>{`Half (${HALF_DAY_RANGE_LABEL})`}</Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: "#F9BC06" }} />
          <Typography sx={{ fontSize: 10, color: "text.secondary" }}>Short (&lt;4h)</Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: "#AECBFA" }} />
          <Typography sx={{ fontSize: 10, color: "text.secondary" }}>Holiday</Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: "#AECBFA" }} />
          <Typography sx={{ fontSize: 10, color: "text.secondary" }}>Sunday</Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

function InsightRow({ icon, color, label, value, hint }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box sx={{ width: 36, height: 36, borderRadius: 2, flexShrink: 0, bgcolor: `${color}15`, color, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{label}</Typography>
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{hint}</Typography>
      </Box>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color }}>{value}</Typography>
    </Stack>
  );
}

function RecordsTable({ records }) {
  const sorted = [...records].sort((a, b) => new Date(b.checkInTime || b.createdAt) - new Date(a.checkInTime || a.createdAt));
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
        <Box component="thead">
          <Box component="tr" sx={{ "& > th": { fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", textAlign: "left", py: 1.25, px: 1, borderBottom: "1px solid", borderColor: "divider" } }}>
            <th>Date</th>
            <th>Check-in</th>
            <th>Check-out</th>
            <th>Hours</th>
            <th>Status</th>
            <th>Source</th>
            <th>Notes</th>
          </Box>
        </Box>
        <Box component="tbody">
          {sorted.map((r) => {
            const d = new Date(r.checkInTime || r.createdAt);
            const hours = resolveRecordWorkingHours(r, resolveAttendanceAsOf(r, d));
            const ci = new Date(r.checkInTime);
            const ciMin = ci.getHours() * 60 + ci.getMinutes();
            const late = ciMin > SHIFT_START_HOUR * 60 + 15;
            const offsite = r.checkInSource === "outside" || r.checkOutSource === "outside";
            return (
              <Box component="tr" key={r._id} sx={{ "&:hover": { bgcolor: "action.hover" }, "& > td": { fontSize: 13, py: 1.25, px: 1, borderBottom: "1px solid", borderColor: "divider" } }}>
                <td>
                  <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</Typography>
                  <Typography sx={{ fontSize: 10, color: "text.secondary" }}>{d.toLocaleDateString("en-IN", { weekday: "short" })}</Typography>
                </td>
                <td>
                  <Typography sx={{ fontSize: 13 }}>{fmtTime(r.checkInTime)}</Typography>
                  {late && <Chip label="Late" size="small" sx={{ height: 16, fontSize: 9, bgcolor: "#FEE2E2", color: "#EF4444", fontWeight: 700, ml: 0 }} />}
                </td>
                <td>{r.checkOutTime ? fmtTime(r.checkOutTime) : <Chip label="Open" size="small" sx={{ height: 16, fontSize: 9, bgcolor: "#FEF3C7", color: "#92400E" }} />}</td>
                <td><Typography sx={{ fontSize: 13, fontWeight: 700 }}>{formatHoursDisplay(hours)}</Typography></td>
                <td>
                  <AttendanceStatusChips hours={hours} />
                </td>
                <td>
                  {offsite
                    ? <Chip icon={<LocationOnOutlinedIcon sx={{ fontSize: "12px !important" }} />} label="Off-site" size="small" sx={{ height: 18, fontSize: 10, bgcolor: "#FEF3C7", color: "#92400E", fontWeight: 700 }} />
                    : <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Office</Typography>
                  }
                </td>
                <td>
                  <Typography sx={{ fontSize: 11, color: "text.secondary", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {[r.checkInLocation, r.checkInReason, r.checkOutLocation, r.checkOutReason].filter(Boolean).join(" • ") || "—"}
                  </Typography>
                </td>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
